/**
 * Schémas Zod de la Recherche globale Ctrl+K (Lot H.5) — partagés API ⇄ ERP.
 *
 * `search.global(q)` interroge N entités en `UNION ALL` (une sous-requête par type,
 * toutes filtrées `domaine_id` sauf les articles d'aide = globaux — cf
 * `03-data-model.md` § 6) et renvoie des résultats GROUPÉS PAR TYPE, chaque groupe
 * borné par `LIMIT`. Priorisation du cadrage métier § 4 (cuvées, n° de lot, clients,
 * n° de documents en priorité 1).
 *
 * GARDE-FOU MÉTIER (non négociable) : un résultat n'apparaît QUE si l'utilisateur a
 * le `*.read` de l'entité (filtrage permission appliqué SERVEUR, bloc par bloc). Le
 * contrat ne porte que la forme des résultats ; l'autorisation vit dans le use case.
 *
 * Chaque résultat expose une `url_cible` (route RELATIVE de l'ERP, ex.
 * `/cave/cuvees/cuv_…`) : la navigation front n'a jamais à reconstruire l'URL.
 */

import { z } from "zod";

// ────────────────────────────────────────────────────────────────────────────
// Référentiel : types de résultats (groupes)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Types de résultats de la recherche globale (un groupe par type). Aligné sur la
 * table de priorisation du cadrage § 4 / `03-data-model.md` § 6. `document` = pièce
 * de vente (devis/commande/BL/facture/avoir) ; `piece_jointe` = fichier GED ;
 * `aide` = article du centre d'aide (seul groupe GLOBAL, non filtré par tenant).
 */
export const RECHERCHE_TYPES = [
  "cuvee",
  "lot",
  "client",
  "document",
  "parcelle",
  "membre",
  "produit",
  "cuve",
  "piece_jointe",
  "aide",
] as const;

/** Type d'un résultat de recherche globale. */
export const rechercheTypeSchema = z.enum(RECHERCHE_TYPES);
export type RechercheType = z.infer<typeof rechercheTypeSchema>;

/** Libellés FR d'affichage des groupes de résultats (en-têtes de la palette). */
export const RECHERCHE_TYPE_LABELS: Record<RechercheType, string> = {
  cuvee: "Cuvées",
  lot: "Lots",
  client: "Clients",
  document: "Documents de vente",
  parcelle: "Parcelles",
  membre: "Équipe",
  produit: "Produits",
  cuve: "Cuves",
  piece_jointe: "Pièces jointes",
  aide: "Aide",
};

// ────────────────────────────────────────────────────────────────────────────
// Input
// ────────────────────────────────────────────────────────────────────────────

/**
 * Borne haute du nombre de résultats PAR GROUPE (defense in depth : la recherche
 * live en `UNION ALL` ne doit pas ramener des centaines de lignes par entité).
 */
export const RECHERCHE_LIMITE_PAR_GROUPE_MAX = 20;

/** Input `search.global` — requête + limite optionnelle par groupe. */
export const searchGlobalInput = z.object({
  /** Texte recherché (n° de lot, nom de cuvée, client…). */
  query: z
    .string()
    .trim()
    .min(2, "Saisissez au moins 2 caractères.")
    .max(120, "Recherche trop longue (120 caractères max)."),
  /** Nombre max de résultats par groupe (défaut serveur : 5). */
  limite: z.number().int().min(1).max(RECHERCHE_LIMITE_PAR_GROUPE_MAX).optional(),
});
export type SearchGlobalInput = z.infer<typeof searchGlobalInput>;

// ────────────────────────────────────────────────────────────────────────────
// Schémas de SORTIE (validation Zod en sortie de tRPC — defense in depth)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Un résultat de recherche. `id` est volontairement une string BRUTE (et non un
 * `idSchema` typé) : les groupes hétérogènes portent des ID de préfixes variés
 * (`cuv_`, `cli_`, `doc_`, `help_`…), tous validés à la source.
 */
export const rechercheResultatSchema = z.object({
  /** Type du résultat (redondant avec le groupe, pratique côté rendu de ligne). */
  type: rechercheTypeSchema,
  /** Identifiant de l'entité (préfixe selon le type). */
  id: z.string(),
  /** Libellé principal affiché (nom de cuvée, n° de lot, raison sociale…). */
  libelle: z.string(),
  /** Libellé secondaire (ville, stade du lot, catégorie…), null si aucun. */
  sous_libelle: z.string().nullable(),
  /** Route RELATIVE de l'ERP vers la fiche cible (navigation directe). */
  url_cible: z.string(),
});
export type RechercheResultat = z.infer<typeof rechercheResultatSchema>;

/** Un groupe de résultats (tous du même `type`). */
export const rechercheGroupeSchema = z.object({
  type: rechercheTypeSchema,
  /** Libellé d'en-tête du groupe (= `RECHERCHE_TYPE_LABELS[type]`). */
  libelle: z.string(),
  /** Résultats du groupe (bornés à la limite par groupe). */
  resultats: z.array(rechercheResultatSchema),
});
export type RechercheGroupe = z.infer<typeof rechercheGroupeSchema>;

/**
 * Sortie `search.global` — résultats GROUPÉS PAR TYPE. Les groupes vides sont
 * OMIS (l'API ne renvoie que les groupes ayant au moins un résultat autorisé) ;
 * `total` est le nombre total de résultats tous groupes confondus.
 */
export const searchGlobalSortieSchema = z.object({
  groupes: z.array(rechercheGroupeSchema),
  total: z.number().int().nonnegative(),
});
export type SearchGlobalSortie = z.infer<typeof searchGlobalSortieSchema>;
